/**
 * L.2: Active Context Pane — collapse state + per-scope active-tab
 * memory.
 *
 * Per-field persistence is intentional (layout-plan §8 note):
 *   - `collapsed`: user preference → localStorage (carries across tabs).
 *   - `activeTabByScope`: work-context → sessionStorage (resets on a
 *     new browser tab — lawyers don't want their last drill-down
 *     echoing into an unrelated fresh tab).
 *
 * A single composite `storage` adapter routes fields to the right
 * backend, so consumers see one store.
 *
 * See: ai-docs/lexide-layout-plan.md §8, ai-docs/lexide-context-pane-plan.md §2
 */

import { create } from "zustand";
import { persist, createJSONStorage, type StateStorage } from "zustand/middleware";
import { createTenantScopedStorage } from "@/lib/tenant-scoped-storage";
import type { ActiveScope } from "@/stores/useActiveContextStore";

export type PaneTab = ActiveScope; // "client" | "matter" | "case"

interface ContextPaneStore {
  collapsed: boolean;
  // Remembers which tab the user last viewed for each selection scope.
  // Example: on a case-scope selection, the user may switch to the
  // Matter tab — next time they open any case, Matter is still the
  // active tab. Same for matter-scope / client-scope selections.
  activeTabByScope: Record<ActiveScope, PaneTab>;

  setCollapsed: (next: boolean) => void;
  toggleCollapsed: () => void;
  setActiveTab: (selectionScope: ActiveScope, tab: PaneTab) => void;
  activeTabFor: (selectionScope: ActiveScope | null | undefined) => PaneTab | null;
}

const LS_KEY = "lexide:context-pane:local";
const SS_KEY = "lexide:context-pane:session";

/** Composite storage that writes `collapsed` to localStorage and
 *  `activeTabByScope` to sessionStorage, transparently merging on read. */
const compositeStorage: StateStorage = {
  getItem: () => {
    if (typeof window === "undefined") return null;
    const localRaw = window.localStorage.getItem(LS_KEY);
    const sessionRaw = window.sessionStorage.getItem(SS_KEY);
    const local = localRaw ? safeParse(localRaw) : null;
    const session = sessionRaw ? safeParse(sessionRaw) : null;
    if (!local && !session) return null;
    return JSON.stringify({
      state: {
        collapsed: local?.state?.collapsed ?? false,
        activeTabByScope:
          session?.state?.activeTabByScope ?? DEFAULT_ACTIVE_TAB_BY_SCOPE,
      },
      version: 0,
    });
  },
  setItem: (_name, value) => {
    if (typeof window === "undefined") return;
    const parsed = safeParse(value);
    const state = parsed?.state ?? {};
    window.localStorage.setItem(
      LS_KEY,
      JSON.stringify({ state: { collapsed: state.collapsed ?? false }, version: 0 }),
    );
    window.sessionStorage.setItem(
      SS_KEY,
      JSON.stringify({
        state: {
          activeTabByScope: state.activeTabByScope ?? DEFAULT_ACTIVE_TAB_BY_SCOPE,
        },
        version: 0,
      }),
    );
  },
  removeItem: () => {
    if (typeof window === "undefined") return;
    window.localStorage.removeItem(LS_KEY);
    window.sessionStorage.removeItem(SS_KEY);
  },
};

function safeParse(raw: string): { state?: Partial<ContextPaneStore> } | null {
  try {
    return JSON.parse(raw) as { state?: Partial<ContextPaneStore> };
  } catch {
    return null;
  }
}

const DEFAULT_ACTIVE_TAB_BY_SCOPE: Record<ActiveScope, PaneTab> = {
  client: "client",
  matter: "matter",
  case: "case",
};

export const useContextPaneStore = create<ContextPaneStore>()(
  persist(
    (set, get) => ({
      collapsed: false,
      activeTabByScope: DEFAULT_ACTIVE_TAB_BY_SCOPE,

      setCollapsed: (next) => set({ collapsed: next }),
      toggleCollapsed: () => set((s) => ({ collapsed: !s.collapsed })),
      setActiveTab: (selectionScope, tab) =>
        set((s) => ({
          activeTabByScope: { ...s.activeTabByScope, [selectionScope]: tab },
        })),
      activeTabFor: (selectionScope) => {
        if (!selectionScope) return null;
        return get().activeTabByScope[selectionScope] ?? selectionScope;
      },
    }),
    {
      name: "lexide:context-pane",
      storage: createJSONStorage(() => createTenantScopedStorage(() => compositeStorage)),
    },
  ),
);
